/**
 * AgentContext (slim) — agent list, workflows & notifications.
 *
 * All WebSocket, messaging, and clarification logic has been extracted into
 * ConnectionContext, MessageContext, and ClarificationContext respectively.
 *
 * Exposes:
 *   agents, setAgents
 *   workflows, setWorkflows
 *   notifications, setNotifications
 *
 * A backward-compatible useAgentContext() hook is also exported — it composes
 * all four contexts so existing consumers can migrate gradually.
 */
import React, { createContext, useContext, useState, useMemo } from 'react';
import { useConnection } from './ConnectionContext';
import { useMessages } from './MessageContext';
import { useClarification } from './ClarificationContext';
import {
  initialAgents,
  initialWorkflows,
  initialNotifications,
} from '../data/seedAgents';

const AgentContext = createContext(null);

export function AgentProvider({ children }) {
  const [agents, setAgents] = useState(initialAgents);
  const [workflows, setWorkflows] = useState(initialWorkflows);
  const [notifications, setNotifications] = useState(initialNotifications);

  const contextValue = useMemo(() => ({
    agents,
    setAgents,
    workflows,
    setWorkflows,
    notifications,
    setNotifications,
  }), [agents, workflows, notifications]);

  return (
    <AgentContext.Provider value={contextValue}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgents() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgents must be used within AgentProvider');
  return ctx;
}

/**
 * Backward-compatible hook — composes all four contexts into a single object
 * matching the original useAgentContext() shape.
 */
export function useAgentContext() {
  const { connectionStatus } = useConnection();
  const {
    conversations,
    setConversations,
    activeConversation,
    setActiveConversation,
    messages,
    sendMessage,
    createConversation,
    setPendingMessage,
  } = useMessages();
  const { pendingClarification, submitClarification } = useClarification();
  const { agents, setAgents, workflows, setWorkflows, notifications, setNotifications } = useAgents();

  return {
    agents,
    setAgents,
    conversations,
    setConversations,
    activeConversation,
    setActiveConversation,
    messages,
    sendMessage,
    createConversation,
    setPendingMessage,
    workflows,
    setWorkflows,
    notifications,
    setNotifications,
    pendingClarification,
    submitClarification,
    connectionStatus,
  };
}
